/**
 * Reads the country out of a lead's free-text `location` ("Austin, Texas,
 * United States", "São Paulo, Brazil, South America", "Greater Boston").
 *
 * Deterministic matching, shared by the outbound sending timezone and the
 * outbound jurisdiction check so the two never disagree about where a lead is.
 */

import { countries } from "country-data-list";

import { getCountryDisplayName, getCountrySearchTerms } from "./countries";
import { AU_STATES, CA_PROVINCES, UK_REGIONS, US_STATES } from "./regions";

/**
 * Names neither country-data-list nor the shared country aliases carry:
 * colloquial forms and the local-language spellings that show up in exports
 * ("Brasil", "Deutschland"), and LinkedIn's labels ("Hong Kong SAR").
 */
const EXTRA_COUNTRY_ALIASES: Record<string, string[]> = {
  US: ["usa", "us", "united states of america"],
  GB: ["uk", "britain", "great britain"],
  IE: ["republic of ireland"],
  NL: ["holland", "the netherlands"],
  DE: ["deutschland"],
  AT: ["osterreich"],
  CH: ["suisse", "schweiz", "svizzera"],
  ES: ["espana"],
  BR: ["brasil"],
  IT: ["italia"],
  SE: ["sverige"],
  NO: ["norge"],
  DK: ["danmark"],
  FI: ["suomi"],
  PL: ["polska"],
  CZ: ["czech republic"],
  GR: ["hellas"],
  TR: ["turkiye"],
  UA: ["kyiv oblast"],
  AE: ["uae"],
  IN: ["bharat"],
  KR: ["south korea", "korea", "republic of korea"],
  VN: ["vietnam"],
  CN: ["mainland china"],
  HK: ["hong kong sar"],
  MO: ["macao sar"],
  RU: ["russian federation"],
  CD: ["congo drc"],
  MK: ["macedonia"],
  BS: ["the bahamas"],
  GM: ["the gambia"],
};

/**
 * Two-letter subdivision codes. Weak on purpose: "San Francisco, CA" means
 * California, but "Berlin, DE" means Germany rather than Delaware, and the
 * string alone can't tell them apart. Callers only act on one when they have
 * no stronger evidence.
 */
const SUBDIVISION_ABBREVIATIONS: Record<string, string> = {
  al: "US",
  ak: "US",
  az: "US",
  ar: "US",
  ca: "US",
  co: "US",
  ct: "US",
  dc: "US",
  de: "US",
  fl: "US",
  ga: "US",
  hi: "US",
  ia: "US",
  id: "US",
  il: "US",
  in: "US",
  ks: "US",
  ky: "US",
  la: "US",
  ma: "US",
  md: "US",
  me: "US",
  mi: "US",
  mn: "US",
  mo: "US",
  ms: "US",
  mt: "US",
  nc: "US",
  nd: "US",
  nh: "US",
  nj: "US",
  nm: "US",
  nv: "US",
  ny: "US",
  oh: "US",
  ok: "US",
  or: "US",
  pa: "US",
  ri: "US",
  sc: "US",
  sd: "US",
  tn: "US",
  tx: "US",
  ut: "US",
  va: "US",
  vt: "US",
  wa: "US",
  wi: "US",
  wv: "US",
  wy: "US",
  ab: "CA",
  bc: "CA",
  mb: "CA",
  nb: "CA",
  ns: "CA",
  on: "CA",
  qc: "CA",
  sk: "CA",
};

/**
 * City / metro labels, for the very common case of a location that names no
 * country ("Greater Boston", "Bengaluru"). Deliberately excludes cities that are
 * ambiguous across countries (Birmingham, Cambridge, Hamilton, Richmond): a
 * wrong country is worse than no match, which just falls back.
 */
const CITY_REGIONS: Record<string, string> = {
  // US metros, in the shape LinkedIn emits once the noise words are stripped
  // ("New York City Metropolitan Area" -> "new york city").
  "new york city": "US",
  nyc: "US",
  brooklyn: "US",
  manhattan: "US",
  "san francisco": "US",
  "san francisco bay": "US",
  "san jose": "US",
  "silicon valley": "US",
  "los angeles": "US",
  "san diego": "US",
  sacramento: "US",
  seattle: "US",
  portland: "US",
  denver: "US",
  "salt lake city": "US",
  phoenix: "US",
  "las vegas": "US",
  austin: "US",
  dallas: "US",
  "dallas fort worth": "US",
  houston: "US",
  "san antonio": "US",
  chicago: "US",
  boston: "US",
  philadelphia: "US",
  pittsburgh: "US",
  atlanta: "US",
  miami: "US",
  orlando: "US",
  tampa: "US",
  charlotte: "US",
  raleigh: "US",
  "raleigh durham": "US",
  "raleigh durham chapel hill": "US",
  nashville: "US",
  minneapolis: "US",
  "minneapolis st paul": "US",
  detroit: "US",
  cleveland: "US",
  columbus: "US",
  cincinnati: "US",
  indianapolis: "US",
  milwaukee: "US",
  "kansas city": "US",
  "st louis": "US",
  "new orleans": "US",
  baltimore: "US",
  "washington dc": "US",
  "washington dc baltimore": "US",
  toronto: "CA",
  vancouver: "CA",
  montreal: "CA",
  calgary: "CA",
  edmonton: "CA",
  ottawa: "CA",
  winnipeg: "CA",
  london: "GB",
  manchester: "GB",
  leeds: "GB",
  glasgow: "GB",
  edinburgh: "GB",
  bristol: "GB",
  cardiff: "GB",
  dublin: "IE",
  lisbon: "PT",
  porto: "PT",
  madrid: "ES",
  barcelona: "ES",
  paris: "FR",
  lyon: "FR",
  brussels: "BE",
  amsterdam: "NL",
  rotterdam: "NL",
  "the hague": "NL",
  berlin: "DE",
  munich: "DE",
  hamburg: "DE",
  frankfurt: "DE",
  cologne: "DE",
  zurich: "CH",
  geneva: "CH",
  vienna: "AT",
  milan: "IT",
  rome: "IT",
  stockholm: "SE",
  oslo: "NO",
  copenhagen: "DK",
  helsinki: "FI",
  warsaw: "PL",
  krakow: "PL",
  prague: "CZ",
  bucharest: "RO",
  athens: "GR",
  istanbul: "TR",
  ankara: "TR",
  kyiv: "UA",
  kiev: "UA",
  "tel aviv": "IL",
  jerusalem: "IL",
  dubai: "AE",
  "abu dhabi": "AE",
  riyadh: "SA",
  jeddah: "SA",
  cairo: "EG",
  lagos: "NG",
  abuja: "NG",
  nairobi: "KE",
  johannesburg: "ZA",
  "cape town": "ZA",
  mumbai: "IN",
  delhi: "IN",
  "new delhi": "IN",
  bengaluru: "IN",
  bangalore: "IN",
  hyderabad: "IN",
  chennai: "IN",
  pune: "IN",
  kolkata: "IN",
  gurugram: "IN",
  noida: "IN",
  karachi: "PK",
  lahore: "PK",
  islamabad: "PK",
  dhaka: "BD",
  "kuala lumpur": "MY",
  jakarta: "ID",
  bangkok: "TH",
  "ho chi minh": "VN",
  "ho chi minh city": "VN",
  hanoi: "VN",
  manila: "PH",
  cebu: "PH",
  shanghai: "CN",
  beijing: "CN",
  shenzhen: "CN",
  guangzhou: "CN",
  tokyo: "JP",
  osaka: "JP",
  seoul: "KR",
  taipei: "TW",
  sydney: "AU",
  melbourne: "AU",
  brisbane: "AU",
  perth: "AU",
  adelaide: "AU",
  auckland: "NZ",
  wellington: "NZ",
  "mexico city": "MX",
  guadalajara: "MX",
  monterrey: "MX",
  "sao paulo": "BR",
  "rio de janeiro": "BR",
  brasilia: "BR",
  "belo horizonte": "BR",
  "buenos aires": "AR",
  santiago: "CL",
  bogota: "CO",
  medellin: "CO",
  lima: "PE",
};

/** Combining diacritical marks, the range NFD decomposition leaves behind. */
const COMBINING_MARKS_FIRST = 0x300;
const COMBINING_MARKS_LAST = 0x36f;

/**
 * Drops the accents NFD split off, so "São" collapses to "Sao" instead of losing
 * the tilde to a space (which would key it as "sa o"). Written as a code-point
 * scan rather than a `\p{Diacritic}` regex: apps/web compiles this package with
 * an ES5 target, which rejects the required `u` flag.
 */
function stripCombiningMarks(value: string): string {
  let stripped = "";
  for (const char of value) {
    const code = char.codePointAt(0) ?? 0;
    if (code < COMBINING_MARKS_FIRST || code > COMBINING_MARKS_LAST) {
      stripped += char;
    }
  }
  return stripped;
}

/**
 * Lowercase, drop accents and punctuation, collapse whitespace. Keeps
 * "São Paulo", "Sao Paulo" and "SAO  PAULO" on one key.
 */
export function normalizeLocation(value: string): string {
  return stripCombiningMarks(value.normalize("NFD"))
    .toLowerCase()
    .replace(/[^a-z0-9\s,]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

interface CountryRow {
  alpha2?: string;
  alpha3?: string;
  name?: string;
  status?: string;
}

/**
 * Subdivisions roll straight up to their country, so "California" only has to
 * tell us "US". Reuses the region lists the location settings forms already own.
 */
const SUBDIVISION_LOOKUP = new Map<string, string>([
  ...US_STATES.map((state) => [normalizeLocation(state), "US"] as const),
  ...CA_PROVINCES.map(
    (province) => [normalizeLocation(province), "CA"] as const,
  ),
  ...UK_REGIONS.map((region) => [normalizeLocation(region), "GB"] as const),
  ...AU_STATES.map((state) => [normalizeLocation(state), "AU"] as const),
  ["district of columbia", "US"] as const,
  ["puerto rico", "US"] as const,
]);

/**
 * Country names -> ISO alpha-2 for every current code: the register name and
 * alpha-3 code from country-data-list, plus the display names and aliases the
 * rest of the product already matches countries by. Alpha-2 codes are skipped:
 * every risky one doubles as a US state or Canadian province abbreviation,
 * which the weak tier handles instead.
 *
 * A country name that is also a subdivision is left to the subdivision:
 * "Atlanta, Georgia" is orders of magnitude more common in our lists than a
 * Tbilisi lead.
 */
const COUNTRY_ROWS: readonly CountryRow[] = countries.all;
const COUNTRY_LOOKUP = new Map<string, string>();
for (const { alpha2, alpha3, name, status } of COUNTRY_ROWS) {
  // Deleted codes get reused ("SK" was Sikkim before it was Slovakia).
  if (!alpha2 || (status !== "assigned" && status !== "user assigned")) {
    continue;
  }
  const names = [
    name,
    alpha3,
    getCountryDisplayName(alpha2),
    ...getCountrySearchTerms(alpha2).filter((term) => term !== alpha2),
  ];
  for (const candidate of names) {
    const key = candidate ? normalizeLocation(candidate) : "";
    if (key && !SUBDIVISION_LOOKUP.has(key)) {
      COUNTRY_LOOKUP.set(key, alpha2);
    }
  }
}
for (const [alpha2, aliases] of Object.entries(EXTRA_COUNTRY_ALIASES)) {
  for (const alias of aliases) {
    COUNTRY_LOOKUP.set(normalizeLocation(alias), alpha2);
  }
}

/**
 * Strips the words LinkedIn-style metro labels wrap a city in, so
 * "Greater Boston Area" and "Boston" hit the same entry.
 */
function stripMetroNoise(token: string): string {
  return token
    .replace(
      /\b(greater|metropolitan|metro|area|region|city of|province of|state of)\b/g,
      " ",
    )
    .replace(/\s+/g, " ")
    .trim();
}

export interface LocationCountryMatch {
  /** ISO 3166-1 alpha-2. */
  country: string;
  /**
   * True for a two-letter subdivision code ("Austin, TX"); see
   * SUBDIVISION_ABBREVIATIONS for why that is weaker than any other match.
   */
  weak: boolean;
}

/** The country a free-text location names, or null when it names none. */
export function matchLocationCountry(raw: string): LocationCountryMatch | null {
  // Right to left: free-text locations read "City, State, Country", so the
  // rightmost token carries the strongest country signal.
  const tokens = normalizeLocation(raw)
    .split(",")
    .map((token) => stripMetroNoise(token.trim()))
    .filter(Boolean)
    .reverse();
  if (tokens.length === 0) {
    return null;
  }

  for (const token of tokens) {
    const country = COUNTRY_LOOKUP.get(token);
    if (country) {
      return { country, weak: false };
    }
  }
  for (const token of tokens) {
    const country = SUBDIVISION_LOOKUP.get(token) ?? CITY_REGIONS[token];
    if (country) {
      return { country, weak: false };
    }
  }
  for (const token of tokens) {
    const country = SUBDIVISION_ABBREVIATIONS[token];
    if (country) {
      return { country, weak: true };
    }
  }
  return null;
}
